import { PayableRow, PayablesPage } from '@bariendo/lib/payables';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { ListRange } from '@/components/Hub/listRange';
import { fetchBotRead } from '@/services/botCalls';

export interface PayablesState {
  payables: PayableRow[];
  hasMore: boolean;
  loading: boolean;
  error: string | undefined;
  reload: () => void;
}

/** Every payable dated in `range`, paid or not (BAR-3046); the tab filters the rest in the browser. */
export function usePayables(range: ListRange): PayablesState {
  const medplum = useMedplum();
  const [page, setPage] = useState<PayablesPage>({ payables: [], hasMore: false, nextCursor: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const { from, to } = range;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchBotRead<{ page: PayablesPage }>(medplum, 'create-treatment-payment', {
      operation: 'payables',
      payableFilters: { status: 'all', from, to },
    })
      .then(({ page: next }) => {
        if (!cancelled) {
          setPage(next);
          setError(undefined);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          // The last range's lines would read as this range's (BAR-3125).
          setPage({ payables: [], hasMore: false, nextCursor: null });
          setError(err instanceof Error ? err.message : 'Could not load payables');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, from, to, reloadKey]);

  return { ...page, loading, error, reload };
}
